import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  APP_MESSAGES,
  environment,
  FZ_ZENDESK_URLS,
  TSEVO_MESSAGE_STATUS_MAP,
} from '@fantaz/shared/app-config';
import { CaptureLocationService } from '@fantaz/shared/data-access/api';
import {
  CasualTournamentDetails,
  User,
} from '@fantaz/shared/data-access/models';
import { ColorScheme } from '@fantaz/shared/data-access/store/src/lib/ui';
import { loadLocation } from '@fantaz/shared/location';
import { ColorSchemeService } from '@fantaz/shared/services/color-scheme';
import { Store } from '@ngrx/store';

@Component({
  selector: 'fz-modal-alert',
  templateUrl: './modal-alert.component.html',
  styleUrls: ['./modal-alert.component.scss'],
})
export class ModalAlertComponent implements OnInit {
  url = FZ_ZENDESK_URLS['location'];
  animateRequiredClass = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      noMargin?: boolean;
      sendActionValue?: boolean;
      textAlign?: string;
      modalType: string;
      customClass?: string;
      showFooterAlert?: boolean;
      footerAlertText?: string;
      image?: string;
      details?: boolean;
      currentUser?: User;
      title?: string;
      description?: string;
      openZenDeskText?: string;
      warningSign?: boolean;
      openZenDeskButtonType?: string;
      tournament?: CasualTournamentDetails;
      primaryActionText?: string;
      secondaryActionText?: string;
      showCloseButton?: boolean; // Show close button
      altDescriptionText?: string;
      altSecondaryActionText?: string;
      required?: boolean;
      hideActions?: boolean;
      hideTitle?: boolean;
      redirectTo?: string;
    },
    private readonly colorSchemeService: ColorSchemeService,
    public dialogRef: MatDialogRef<ModalAlertComponent>,
    private locationService: CaptureLocationService,
    private store: Store,
  ) {
    this.colorSchemeService.init();

    // remove undefined keys form this.data
    this.data = Object.keys(this.data).reduce((acc: any, key: string) => {
      if (this.data[key as keyof typeof this.data]) {
        acc[key] = this.data[key as keyof typeof this.data];
      }
      return acc;
    }, {});
  }

  ngOnInit(): void {
    switch (this.data.modalType) {
      case 'LOCATION_ERROR':
      case 'LOCATION_NOT_ALLOWED':
      case 'location-error':
        this.data = {
          ...this.data,
          modalType: 'location-error',
          showCloseButton: true,
          showFooterAlert: true,
          footerAlertText: 'Are you connected to a VPN or another network?',
          image: `/shared/svg/${
            this.isDarkScheme() ? 'jurisdiction-dark' : 'jurisdiction'
          }.svg`,
          customClass: this.data.customClass ?? '',
          title: `You are not in a legal jurisdiction, you won't be able to play this time.`,
          primaryActionText: 'Understood',
          secondaryActionText: '',
        };
        break;
      case 'WALLET_FROZEN_BY_ADMIN':
        this.data = {
          customClass: 'padding-image',
          modalType: 'WALLET_FROZEN_BY_ADMIN',
          image: '/shared/img/wallet/wallet-disabled.png',
          title: APP_MESSAGES['WALLET_FROZEN_BY_ADMIN'],
          primaryActionText: '',
          openZenDeskText: 'Contact Customer support',
          secondaryActionText: '',
        };
        break;
      case 'TRANSACTION_NOT_FOUND':
        this.data = {
          description: this.data.description,
          customClass: 'padding-image',
          modalType: 'TRANSACTION_NOT_FOUND',
          showCloseButton: true,
          image: '/shared/img/wallet/withdraw.png',
          primaryActionText: '',
          openZenDeskText: 'Contact Customer support',
          secondaryActionText: '',
        };
        break;
      case 'WALLET_PROCESSOR_ERROR':
      case 'UNKNOWN_ERROR':
        this.data = {
          ...this.data,
          customClass: 'padding-image',
          modalType: 'WALLET_FROZEN_BY_ADMIN',
          image: '/shared/svg/wallet/payment-processor-error.svg',
          title:
            this.data.title ??
            TSEVO_MESSAGE_STATUS_MAP['UNKNOWN_ERROR'].textContent,
          primaryActionText: '',
          openZenDeskText: 'Contact Customer support',
          secondaryActionText: '',
        };
        break;
      case 'ACCOUNT_ALREADY_EXISTS':
        this.data = {
          ...this.data,
          title: 'We found a duplication',
          customClass: 'big-buttons',
          secondaryActionText: 'Logout to use this account',
          openZenDeskText: 'Contact Customer Support',
          altDescriptionText:
            'Are you trying to register a business account as its legal representative?',
          altSecondaryActionText: 'Open a Business Account',
        };
        break;
      case 'WALLET_FROZEN_BY_TAX_INFORMATION_REQUIRED':
        this.data = {
          customClass: 'padding-image',
          modalType: 'WALLET_FROZEN_BY_TAX_INFORMATION_REQUIRED',
          image: '/shared/img/wallet/wallet-disabled.png',
          title: APP_MESSAGES['WALLET_FROZEN_BY_TAX_INFORMATION_REQUIRED'],
          primaryActionText: 'Fill tax information',
          secondaryActionText: '',
        };
        break;
      case 'LOCATION_DISABLED':
      case 'location-disabled':
        this.data = {
          ...this.data,
          modalType: 'location-disabled',
          image: `/shared/svg/${
            this.isDarkScheme() ? 'jurisdiction-dark' : 'jurisdiction'
          }.svg`,
          details: true,
          title:
            'For compliance purposes, we would like to validate your location.',
          primaryActionText: 'Share Location',
        };
        break;

      case 'WALLET_BALANCE_NEGATIVE':
        this.data = {
          image: `/shared/svg/${
            this.isDarkScheme() ? 'load-wallet-dark' : 'load-wallet'
          }.svg`,
          title: 'Your wallet has insufficient balance.',
          description: `Please make a deposit to continue playing in your favorite tournaments.`,
          primaryActionText: this.data.primaryActionText ?? 'Load My Z-Wallet',
          secondaryActionText: '',
          showCloseButton: true,
          ...this.data,
          modalType: 'balance-error',
        };
        break;
      case 'WALLET_BALANCE':
      case 'NOT_ENOUGH_BALANCE':
      case 'balance-error':
        this.data = {
          image: `/shared/svg/${
            this.isDarkScheme() ? 'load-wallet-dark' : 'load-wallet'
          }.svg`,
          title: 'Almost there!',
          description: `I looks like you don't have enough funds in your Z-Wallet to join the paid tournament. Don't worry, you can easily add funds to continue. Please load your wallet and start having fun!`,
          primaryActionText: this.data.primaryActionText ?? 'Load My Z-Wallet',
          secondaryActionText: '',
          showCloseButton: true,
          ...this.data,
          modalType: 'balance-error',
        };
        break;
      case 'UNAUTHENTICATED':
      case 'login-error':
        this.data = {
          ...this.data,
          modalType: 'login-error',
          image:
            this.colorSchemeService.get() === ColorScheme.Dark
              ? '/shared/svg/log-in-dark.svg'
              : '/shared/svg/log-in.svg',
          title:
            this.data.title ??
            'You need to log in to start playing our tournaments.',
          primaryActionText: 'Log in | Sign up',
          secondaryActionText: '',
          showCloseButton: true,
          customClass: this.data.customClass ?? 'action-buttons-row',
        };
        break;
      case 'SESSION_EXPIRED':
        this.data = {
          ...this.data,
          modalType: 'login-error',
          image: '/shared/svg/log-in.svg',
          title:
            this.data.title ??
            'Your session has expired. Please login again to continue.',
          primaryActionText: 'Log In',
          secondaryActionText: '',
        };
        break;
      case 'IDENTITY_ERROR':
      case 'identity-error':
        this.setIdentityCaseData();
        break;
      case 'IMAGE_VALIDATED':
        this.data = {
          ...this.data,
          modalType: 'IMAGE_VALIDATED',
          showCloseButton: true,
          image: '/shared/svg/verified-image.svg',
          title: 'Your verification is in progress!',
          description:
            "We'll keep you posted once it's done! In the meantime, feel free to check out our exciting tournaments by clicking the button below.",
          primaryActionText: 'Check out our tournaments',
        };
        break;
      case 'GAME_BLOCKED':
      case 'game-blocked':
        this.data = {
          ...this.data,
          modalType: 'game-blocked',
          image: '/shared/img/history-empty.png',
          title:
            'The game is not currently supported on your device/browser. Please check out the list of tournaments available for this device.',
          primaryActionText: 'Tournaments',
          secondaryActionText: '',
        };
        break;
      case 'ZBO_ONLY':
        this.setZBOData();
        break;

      case 'VALID_IMAGE':
        this.data = {
          ...this.data,
          modalType: 'VALID_IMAGE',
          image: '/shared/svg/imageValid.svg',
          warningSign: true,
          title: 'Thank you! We are reviewing your profile... ',
          description:
            'Typically, the verification process just takes a few minutes, but in some special circumstances, out of our control, could take up to 48h.',
          primaryActionText: 'Go to play page',
          secondaryActionText: 'Back to wallet',
        };
        break;

      case 'UNVERIFIED':
        this.data = {
          ...this.data,
          image: `/shared/img/wallet/${
            this.isDarkScheme() ? 'withdraw-dark' : 'withdraw'
          }.png`,
          title: `Apologies, We were unable to verify your identity. Please report this to customer service as soon as possible.`,
          openZenDeskText: 'Contact Customer support',
        };
        break;

      case 'WITHDRAWAL_MIN_AMOUNT_LIMIT':
        this.data = {
          ...this.data,
          image: `/shared/img/wallet/${
            this.isDarkScheme() ? 'withdraw-dark' : 'withdraw'
          }.png`,
          title: TSEVO_MESSAGE_STATUS_MAP['WITHDRAWAL_MIN_AMOUNT_LIMIT'].name,
          description:
            TSEVO_MESSAGE_STATUS_MAP['WITHDRAWAL_MIN_AMOUNT_LIMIT'].textContent,
          openZenDeskText:
            TSEVO_MESSAGE_STATUS_MAP['WITHDRAWAL_MIN_AMOUNT_LIMIT'].button
              .content,
        };
        break;

      case 'WITHDRAWAL_MAX_AMOUNT_LIMIT':
        this.data = {
          ...this.data,
          image: `/shared/img/wallet/${
            this.isDarkScheme() ? 'withdraw-dark' : 'withdraw'
          }.png`,
          title: TSEVO_MESSAGE_STATUS_MAP['WITHDRAWAL_MAX_AMOUNT_LIMIT'].name,
          description:
            TSEVO_MESSAGE_STATUS_MAP['WITHDRAWAL_MAX_AMOUNT_LIMIT'].textContent,
          openZenDeskText:
            TSEVO_MESSAGE_STATUS_MAP['WITHDRAWAL_MAX_AMOUNT_LIMIT'].button
              .content,
        };
        break;

      case 'WITHDRAWAL_MAX_REQUEST_LIMIT':
        this.data = {
          ...this.data,
          image: `/shared/img/wallet/${
            this.isDarkScheme() ? 'withdraw-dark' : 'withdraw'
          }.png`,
          title: TSEVO_MESSAGE_STATUS_MAP['WITHDRAWAL_MAX_REQUEST_LIMIT'].name,
          description:
            TSEVO_MESSAGE_STATUS_MAP['WITHDRAWAL_MAX_REQUEST_LIMIT']
              .textContent,
          openZenDeskText:
            TSEVO_MESSAGE_STATUS_MAP['WITHDRAWAL_MAX_REQUEST_LIMIT'].button
              .content,
        };
        break;

      case 'DEPOSIT_MIN_AMOUNT_LIMIT':
        this.data = {
          ...this.data,
          image: `/shared/svg/${
            this.isDarkScheme() ? 'load-wallet-dark' : 'load-wallet'
          }.svg`,
          title: TSEVO_MESSAGE_STATUS_MAP['DEPOSIT_MIN_AMOUNT_LIMIT'].name,
          description:
            TSEVO_MESSAGE_STATUS_MAP['DEPOSIT_MIN_AMOUNT_LIMIT'].textContent,
          openZenDeskText:
            TSEVO_MESSAGE_STATUS_MAP['DEPOSIT_MIN_AMOUNT_LIMIT'].button.content,
        };
        break;

      case 'DEPOSIT_MAX_AMOUNT_LIMIT':
        this.data = {
          ...this.data,
          image: `/shared/svg/${
            this.isDarkScheme() ? 'load-wallet-dark' : 'load-wallet'
          }.svg`,
          title: TSEVO_MESSAGE_STATUS_MAP['DEPOSIT_MAX_AMOUNT_LIMIT'].name,
          description:
            TSEVO_MESSAGE_STATUS_MAP['DEPOSIT_MAX_AMOUNT_LIMIT'].textContent,
          openZenDeskText:
            TSEVO_MESSAGE_STATUS_MAP['DEPOSIT_MAX_AMOUNT_LIMIT'].button.content,
        };
        break;

      case 'REQUEST_WITHDRAWAL_SUCCESS':
        this.data = {
          ...this.data,
          image: `/shared/img/wallet/${
            this.isDarkScheme() ? 'withdraw-dark' : 'withdraw'
          }.png`,
          title:
            'We have received your Withdrawal request and will process this in 3-5 business days.',
          primaryActionText: 'Understood',
          customClass: 'big-buttons',
        };
        break;

      case 'SUCCESS_ZBO':
        this.data = {
          ...this.data,
          modalType: 'SUCCESS_ZBO',
          image: '/shared/img/zbo_success.png',
          title: 'Great news <b>ZBO!</b><br>You can compete now!',
          secondaryActionText: 'Close',
        };
        break;

      case 'CONFIRMATION':
        this.data = {
          ...this.data,
          modalType: 'CONFIRMATION',
        };
        break;
    }

    // add animation when user clicks on the modal
    if (this.data?.required) {
      this.dialogRef.backdropClick().subscribe((result) => {
        this.animateRequiredClass = true;

        setTimeout(() => {
          this.animateRequiredClass = false;
        }, 1500);
      });
    }
  }

  private setIdentityCaseData() {
    const image =
      this.colorSchemeService.get() === ColorScheme.Dark
        ? '/shared/svg/log-in-dark.svg'
        : '/shared/svg/log-in.svg';
    this.data = {
      image,
      title: 'Almost there!',
      description:
        "Please complete your player profile before joining a tournament. It only takes a moment. We can't wait to have you playing with us. Start having fun!",
      primaryActionText: 'Set up my player profile',
      ...this.data,
      modalType: 'identity-error',
      showCloseButton: true,
    };
  }

  private setZBOData() {
    const img =
      this.colorSchemeService.get() === ColorScheme.Dark
        ? '/shared/svg/zbo-dark.svg'
        : '/shared/svg/zbo.svg';
    this.data = {
      ...this.data,
      modalType: 'ZBO_ONLY',
      image: img,
      details: false,
      showCloseButton: true,
      title:
        'This is a <b>ZBO only</b> tournament.</b><br> Please upgrade your account to compete</b><br>in this tournament.',
      primaryActionText: 'Become a ZBO',
      secondaryActionText: 'Not Now',
      customClass: 'zbo-modal',
    };
  }

  onSecondaryClick(): void {
    this.closeModal('secondary');
    if (this.data.modalType === 'ACCOUNT_ALREADY_EXISTS') {
      this.redirectToLogin();
    }
  }

  closeModal(action?: string): void {
    if (this.data.modalType !== 'ACCOUNT_ALREADY_EXISTS') {
      if (this.data.sendActionValue && action) {
        this.dialogRef.close({ action });
      } else {
        this.dialogRef.close(true);
      }
    }
  }

  onPrimaryClick(): void {
    if (
      this.data.modalType === 'login-error' ||
      this.data.modalType === 'UNAUTHENTICATED'
    ) {
      this.dialogRef.close(false);
      this.redirectToLogin();
    } else if (
      this.data.modalType === 'balance-error' ||
      this.data.modalType === 'WALLET_BALANCE'
    ) {
      this.closeModal();
      const url = new URL('/wallet', environment.baseURL);
      url.searchParams.append('redirectTo', window.location.href);
      window.location.href = url.toString();
    } else if (
      this.data.modalType === 'location-disabled' ||
      this.data.modalType === 'LOCATION_DISABLED'
    ) {
      this.onEnableLocation();
    } else if (
      this.data.modalType === 'identity-error' ||
      this.data.modalType === 'IDENTITY_ERROR'
    ) {
      this.closeModal();
      let url = new URL('/user-game-profile', environment.baseURL);
      url.searchParams.append('redirectTo', window.location.href);

      if (this.data.redirectTo) {
        url = new URL(this.data.redirectTo);
      }

      window.location.href = url.toString();
      return;
    } else if (
      this.data.modalType === 'location-error' ||
      this.data.modalType === 'location-disabled' ||
      this.data.modalType === 'LOCATION_ERROR' ||
      this.data.modalType === 'LOCATION_DISABLED'
    ) {
      this.dialogRef.close(false);
    } else if (
      this.data.modalType === 'WALLET_FROZEN_BY_TAX_INFORMATION_REQUIRED'
    ) {
      window.location.href = '/profile/tax-form';
    } else if (this.data.modalType === 'ZBO_ONLY') {
      this.closeModal();
      const url = new URL('/profile/subscriptions', environment.baseURL);
      url.searchParams.append('redirectTo', window.location.href);
      url.searchParams.append('tournamentForZboOnly', 'true');
      window.location.href = url.toString();
    } else if (
      this.data.modalType === 'game-blocked' ||
      this.data.modalType === 'GAME_BLOCKED' ||
      this.data.modalType === 'IMAGE_VALIDATED'
    ) {
      window.location.href = '/tournaments';
      return;
    } else if (this.data.modalType === 'CONFIRMATION') {
      this.dialogRef.close({ confirm: true });
    } else {
      this.closeModal('primary');
    }
  }

  onAltSecondaryClick(): void {
    if (this.data.modalType === 'ACCOUNT_ALREADY_EXISTS') {
      window.location.href = '/user-game-profile?profile-type=business';
    }
  }

  redirectToLogin() {
    const params = new URLSearchParams(window.location.search);
    const inviteBy = params.get('inviteBy') || '';
    let url = new URL('/auth/sign-in', environment.baseURL);
    if (!inviteBy) {
      url.searchParams.append('redirectTo', window.location.pathname);
    } else {
      const joinUrl = localStorage.getItem('joinUrl') || '';
      if (joinUrl != '') {
        url = new URL(joinUrl);
      }
    }
    window.location.href = url.toString();
  }

  onEnableLocation() {
    this.locationService.setForce(true);
    this.store.dispatch(loadLocation());
    this.dialogRef.close(true);
  }

  isDarkScheme() {
    return this.colorSchemeService.get() === ColorScheme.Dark;
  }
}
